/**
 * Cad2DCellViewport — one read-only preview cell of the model-viewport
 * tiling (Faz 4.9). Mirrors the 3D workbench's MiniViewport pattern: its
 * own lightweight canvas mounting the shared document read-only, an
 * orthographic plan camera posed from the cell's own zoom/target, wheel
 * zoom + middle-drag pan writing back to useModelViewportsStore so every
 * cell frames independently. A click hands the cell the interactive
 * viewport (the command line's target).
 */

import { useRef, useCallback } from 'react';
import { Box, HStack, Text } from '@chakra-ui/react';
import * as THREE from 'three';
import { Canvas, useThree } from '@react-three/fiber';
import { EntityLayer } from '../../rendering/EntityLayer';
import { AdaptiveCadGrid } from '../../rendering/AdaptiveCadGrid';
import { DemandRenderController } from '../../rendering/RenderLoopController';
import { PreviewViewportContext } from '../../rendering/toolEventBus';
import { useAppStore } from '../../state/store';
import { useModelViewportsStore } from '../../state/useModelViewportsStore';
import { useViewportThemeColors } from '../../hooks/useThemeInk';

const POSE_DISTANCE = 100;

/** Camera rig: poses the ortho plan camera from the cell's stored view. */
function CellCameraRig({ cell }: { cell: number }) {
    // Poses ride the store; the component re-poses on every change.
    const zoom = useModelViewportsStore((s) => s.cells[cell]?.zoom ?? 20);
    const target = useModelViewportsStore((s) => s.cells[cell]?.target ?? [0, 0]);
    const camera = useThree((s) => s.camera) as THREE.OrthographicCamera;
    camera.up.set(0, 1, 0);
    camera.position.set(target[0], target[1], POSE_DISTANCE);
    camera.zoom = zoom;
    camera.lookAt(target[0], target[1], 0);
    camera.updateProjectionMatrix();
    return null;
}

/** Cell-local grid following the stored zoom (drafting reference only). */
function CellGrid({ cell }: { cell: number }) {
    const gridColors = useViewportThemeColors('2d');
    const zoom = useModelViewportsStore((s) => s.cells[cell]?.zoom ?? 20);
    void zoom; // AdaptiveCadGrid reads the camera zoom itself.
    return <AdaptiveCadGrid cellColor={gridColors.gridMajor} sectionColor={gridColors.gridSection} />;
}

export function Cad2DCellViewport({ cell }: { cell: number }) {
    const gridVisible = useAppStore((s) => s.view.gridVisible);
    // The active 2D drawing view filter — preview cells show exactly what
    // the interactive viewport shows (same EntityLayer view gate).
    const viewId = useAppStore((s) => s.activeDrawingViewId);
    const setActiveCell = useModelViewportsStore((s) => s.setActiveCell);
    const panState = useRef<{ x: number; y: number; target: [number, number] } | null>(null);

    const onWheel = useCallback((e: React.WheelEvent) => {
        const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
        const pose = useModelViewportsStore.getState().cells[cell] ?? { zoom: 20, target: [0, 0] as [number, number] };
        useModelViewportsStore.getState().setCellPose(cell, {
            zoom: Math.min(Math.max(pose.zoom * factor, 0.01), 100000),
        });
    }, [cell]);

    const onPointerDown = useCallback((e: React.PointerEvent) => {
        setActiveCell(cell);
        if (e.button !== 1) return; // middle button = pan
        e.preventDefault();
        const pose = useModelViewportsStore.getState().cells[cell] ?? { zoom: 20, target: [0, 0] as [number, number] };
        panState.current = { x: e.clientX, y: e.clientY, target: [...pose.target] as [number, number] };
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
    }, [cell, setActiveCell]);

    const onPointerMove = useCallback((e: React.PointerEvent) => {
        const start = panState.current;
        if (!start) return;
        const zoomNow = useModelViewportsStore.getState().cells[cell]?.zoom ?? 20;
        const worldPerPx = 1 / Math.max(zoomNow, 1e-6);
        const dx = (e.clientX - start.x) * worldPerPx;
        const dy = (e.clientY - start.y) * worldPerPx;
        // Natural grab: content follows the cursor.
        useModelViewportsStore.getState().setCellPose(cell, {
            target: [start.target[0] - dx, start.target[1] + dy],
        });
    }, [cell]);

    const onPointerUp = useCallback(() => { panState.current = null; }, []);

    return (
        <Box
            w="100%"
            h="100%"
            position="relative"
            bg="var(--color-bg-viewport-2d)"
            onWheel={onWheel}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            title="Click to make this the active viewport"
        >
            <Canvas frameloop="demand" dpr={[1, 1.5]} orthographic camera={{ zoom: 20, near: 0.1, far: POSE_DISTANCE * 20 }}>
                <PreviewViewportContext.Provider value={true}>
                    <CellCameraRig cell={cell} />
                    <DemandRenderController />
                    {gridVisible && <CellGrid cell={cell} />}
                    <EntityLayer cad2D viewId={viewId} />
                </PreviewViewportContext.Provider>
            </Canvas>
            <HStack position="absolute" top={1} left={2} spacing={1} pointerEvents="none">
                <Text fontSize="xs" color="text.secondary" fontWeight="bold" textTransform="uppercase">
                    {`VPORTS ${cell + 1}`}
                </Text>
            </HStack>
        </Box>
    );
}
